import { Link, getRouteApi } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { NavBreadcrumbs } from "@/components/NavBreadcrumbs";
import {
  collectionScheduleParams,
  getCollection,
} from "@/lib/series-collections";
import "./schedule.$collectionKey.css";

const routeApi = getRouteApi("/schedule/$collectionKey");

function formatEventDate(date: string | null): string {
  if (!date) return "Date TBD";
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "Date TBD";
  return parsed.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatRaceType(raceType: string | null): string | null {
  if (!raceType) return null;
  return raceType
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function SchedulePage() {
  const { collectionKey } = routeApi.useParams();
  const collection = getCollection(collectionKey);
  const [year, setYear] = useState(() => new Date().getFullYear());

  const scheduleQuery = useQuery({
    queryKey: ["schedule", collectionKey, year],
    queryFn: async () => {
      if (!collection) throw new Error("Unknown collection");
      const { data, error } = await api.GET("/events/schedule", {
        params: { query: collectionScheduleParams(collection, year) },
      });
      if (error) throw error;
      return data;
    },
    enabled: Boolean(collection),
  });

  if (!collection) {
    return (
      <main className="schedule-page">
        <NavBreadcrumbs items={[{ label: "Home", to: "/" }]} current="Schedule" />
        <h1 className="schedule-title">Collection not found</h1>
        <p className="schedule-empty">
          We don't have a schedule for "{collectionKey}".
        </p>
      </main>
    );
  }

  const data = scheduleQuery.data;
  const minYear = data?.minYear ?? year - 5;
  const maxYear = data?.maxYear ?? year + 1;
  const years: number[] = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y);

  return (
    <main className="schedule-page">
      <NavBreadcrumbs
        items={[{ label: "Home", to: "/" }]}
        current={collection.label}
      />

      <header className="schedule-header">
        {collection.logo ? (
          <img
            src={collection.logo}
            alt={collection.label}
            className="schedule-logo"
          />
        ) : null}
        <div>
          <h1 className="schedule-title">{collection.label}</h1>
          <p className="schedule-subtitle">Event schedule by year</p>
        </div>
      </header>

      <div className="schedule-years" role="tablist" aria-label="Select year">
        {years.map((y) => (
          <button
            key={y}
            type="button"
            role="tab"
            aria-selected={y === year}
            className={`schedule-year-btn${y === year ? " active" : ""}`}
            onClick={() => setYear(y)}
          >
            {y}
          </button>
        ))}
      </div>

      {scheduleQuery.isLoading ? (
        <p className="schedule-empty">Loading schedule…</p>
      ) : scheduleQuery.isError ? (
        <p className="schedule-empty">Couldn't load the schedule. Try again.</p>
      ) : !data || data.events.length === 0 ? (
        <p className="schedule-empty">No events scheduled for {year}.</p>
      ) : (
        <ul className="schedule-list">
          {data.events.map((event) => {
            const raceType = formatRaceType(event.raceType);
            const row = (
              <>
                <span className="schedule-date">
                  {formatEventDate(event.eventDate)}
                </span>
                <span className="schedule-name">{event.name}</span>
                {raceType ? (
                  <span className="schedule-badge">{raceType}</span>
                ) : null}
              </>
            );
            return (
              <li key={event.eventId} className="schedule-row">
                {event.seriesId ? (
                  <Link
                    to="/events/series/$seriesId"
                    params={{ seriesId: event.seriesId }}
                    search={{ year: undefined }}
                    className="schedule-row-link"
                  >
                    {row}
                  </Link>
                ) : (
                  <div className="schedule-row-link">{row}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
